/**
 * Phase 2 — Section 6: Post-onboarding "AI suggestions available" banner.
 * Reusable across every module that benefits from enrichment. Appears ONLY
 * when the org has enrichment data. On "Load Suggestions" it calls
 * POST /api/enrichment/ai-suggest-module-content and renders the results with
 * a "Suggested" badge. The BCM Admin can Accept All / Accept Selected / Dismiss.
 *
 * Usage:
 *   <AISuggestionsBanner module="risk_assessment" onAccept={(items)=>{...}} />
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { extractErrorMessage } from "../utils/acceptSuggestions";
import { Sparkles, RefreshCw, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { getEnrichmentContext } from "../utils/enrichmentContext";
import { EnrichmentContextStrip } from "./EnrichmentDataChip";

const MODULE_LABELS = {
  mission_vision: "Mission & Vision",
  team_personnel: "BCM Team & Personnel",
  service_lines: "Service Lines",
  stakeholder_register: "Stakeholder Register",
  infrastructure_applications: "Infrastructure & Applications",
  critical_functions: "Critical Business Functions",
  risk_assessment: "Risk Assessment",
  threat_profiling: "Threat Profiling",
  recovery_plans: "Recovery Plans",
  crisis_communication: "Crisis Communication",
  emergency_response: "Emergency Response",
  training_awareness: "Training & Awareness",
  exercise_scenarios: "Exercise Scenarios",
  kpi_targets: "KPI Targets",
  audit_readiness: "Audit Readiness",
  improvement_actions: "Improvement Actions",
  management_review: "Management Review",
};

/** Presentation helpers: readable labels + values (no raw JSON/brackets). */
const _ABBREV = new Set(["kpi", "rto", "rpo", "mtpd", "bia", "it", "id", "url", "ai", "sla", "roi", "drp", "bcp", "hr", "sop", "api", "pdca", "kri"]);
const _humanKey = (k) => String(k)
  .replace(/_/g, " ")
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // split camelCase into words
  .split(/\s+/)
  .filter(Boolean)
  .map((w) => (_ABBREV.has(w.toLowerCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
  .join(" ");
// Whole-key overrides for anything title-casing shouldn't touch; falls back to
// _humanKey (title-case + abbreviation handling) for every other payload key.
const FIELD_LABELS = {
  summary: "Summary",
  recommendation: "Recommendation",
  recommendations: "Recommendations",
  overview: "Overview",
  rationale: "Rationale",
  narrative: "Narrative",
  conclusion: "Conclusion",
  notes: "Notes",
  details: "Details",
  description: "Description",
};
const _fieldLabel = (k) => FIELD_LABELS[k] || _humanKey(k);
const _formatVal = (v) => Array.isArray(v)
  ? v.map((x) => (x && typeof x === "object" ? Object.values(x).join(" — ") : String(x))).join(", ")
  : (v && typeof v === "object" ? Object.values(v).map(String).join(", ") : String(v));
const _TITLE_KEYS = ["name", "role", "function_name", "risk", "kpi_name", "module_name",
  "scenario_name", "checklist_item", "action", "threat", "plan_name", "title"];

/** Flatten the module-specific suggestion object into renderable line items. */
function flatten(suggestions) {
  if (!suggestions || typeof suggestions !== "object") return [];
  const out = [];
  for (const [key, val] of Object.entries(suggestions)) {
    if (key.startsWith("_")) continue;
    if (Array.isArray(val)) {
      // management_review-only tagging: metrics vs flags are indistinguishable once
      // flattened to plain strings, and the banner only forwards `detail || label` to
      // onAccept. Prefix the label (survives as the forwarded value) so the Management
      // Review compose handler can route items. Empty prefix for every other module —
      // their flatten output is byte-for-byte unchanged.
      const _mrPrefix = key === "key_metrics" ? "Metric — " : key === "change_flags" ? "Change flag — " : "";
      const _mrKey = (key === "key_metrics" || key === "change_flags") ? key : null;
      val.forEach((item, i) => {
        const _isObj = item && typeof item === "object";
        const _text = _isObj
          ? (item.name || item.role || item.function_name || item.risk || item.kpi_name ||
             item.module_name || item.scenario_name || item.checklist_item || item.action ||
             item.threat || item.plan_name || item.title || _formatVal(Object.values(item)[0]))
          : String(item);
        out.push({
          id: `${key}-${i}`,
          label: _mrPrefix + _text,
          detail: _isObj ? (_mrKey ? { ...item, __mrKey: _mrKey } : item) : null,
        });
      });
    } else if (typeof val === "string" && val.trim()) {
      out.push({ id: key, label: `${_fieldLabel(key)}: ${val}`, detail: null });
    }
  }
  return out;
}

export default function AISuggestionsBanner({ module, orgId: orgIdProp, onAccept, existingKeys = [], itemKeyOf, refreshKey, className = "" }) {
  const { api, user } = useAuth();
  const orgId = orgIdProp || user?.org_id;
  const _norm = (s) => String(s ?? "").trim().toLowerCase();
  const _keyOf = (it) => _norm(itemKeyOf ? itemKeyOf(it.detail || it.label) : (it.label || ""));
  const _existingSet = new Set((existingKeys || []).map(_norm));
  const [hasEnrichment, setHasEnrichment] = useState(false);
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState({});
  const [expanded, setExpanded] = useState(true);
  const [acceptedIds, setAcceptedIds] = useState(() => new Set());
  // Derive badge state at RENDER time (never from a stale closure): a suggestion is
  // "accepted" if accepted this session, "already_added" if it matches an existing
  // DB record (case-insensitive), else "suggested".
  const _badgeState = (it) =>
    acceptedIds.has(it.id) ? "accepted" : (_existingSet.has(_keyOf(it)) ? "already_added" : "suggested");
  const decorated = (items || []).map((it) => ({ ...it, badgeState: _badgeState(it) }));

  // Only show the banner if enrichment data exists for the org. Also pull the
  // normalized enrichment context (Part 0) so we can show the data chips.
  useEffect(() => {
    let active = true;
    // Reset any previously loaded suggestions/context when the org's enrichment
    // changes (refreshKey), so stale chips/suggestions from a prior domain clear.
    setItems(null);
    setContext(null);
    setAcceptedIds(new Set());
    (async () => {
      if (!orgId) return;
      try {
        const res = await api.get(`/enrichment/status/${orgId}`);
        if (active) setHasEnrichment((res.data?.fields_populated || []).length > 0);
      } catch { /* no enrichment -> banner hidden */ }
      try {
        const ctx = await getEnrichmentContext(api, orgId, { force: true });
        if (active) setContext(ctx);
      } catch { /* ignore */ }
    })();
    return () => { active = false; };
  }, [api, orgId, refreshKey]);

  const loadSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.post("/enrichment/ai-suggest-module-content", { org_id: orgId, module });
      const flat = flatten(res.data?.suggestions);
      setItems(flat);
      setAcceptedIds(new Set());
      setSelected(Object.fromEntries(flat.map((f) => [f.id, true])));
      if (flat.length === 0) toast.info("No suggestions generated for this module yet.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load AI suggestions");
    } finally { setLoading(false); }
  }, [api, orgId, module]);

  const [accepting, setAccepting] = useState(false);
  const canAccept = typeof onAccept === "function";

  const accept = useCallback(async (onlySelected) => {
    const chosen = decorated.filter((it) =>
      (!onlySelected || selected[it.id]) && it.badgeState !== "accepted");
    if (chosen.length === 0) { toast.error("Nothing selected"); return; }

    const dupes = chosen.filter((it) => it.badgeState === "already_added");
    const fresh = chosen.filter((it) => it.badgeState !== "already_added");
    let toCreate = chosen;

    if (dupes.length > 0) {
      if (chosen.length === 1) {
        if (!window.confirm("A record with this name already exists. Create a duplicate?")) return;
      } else {
        const choice = window.confirm(
          `${dupes.length} of ${chosen.length} suggestions match existing records.\n\n` +
          `OK = Create All (including duplicates)\nCancel = Skip duplicates (create only the ${fresh.length} new)`
        );
        toCreate = choice ? chosen : fresh;
        if (toCreate.length === 0) { toast("No new suggestions to add"); return; }
      }
    }

    const newlyAccepted = toCreate.map((c) => c.id);
    const payload = toCreate.map((c) => c.detail || c.label);
    setAccepting(true);
    try {
      const result = await onAccept(payload);
      const created = (result && typeof result === "object" && "created" in result) ? result.created : toCreate.length;
      if (result && typeof result === "object" && result.error && !result.created) {
        toast.error(`Could not add suggestions: ${result.error}`);
      } else {
        toast.success(`Added ${created} item${created === 1 ? "" : "s"}`);
        if (result?.error) toast(`Some items failed: ${result.error}`);
        // Keep suggestions visible; mark the accepted ones green instead of clearing.
        setAcceptedIds((prev) => new Set([...prev, ...newlyAccepted]));
      }
    } catch (e) {
      toast.error(`Could not add suggestions: ${extractErrorMessage(e)}`);
    } finally {
      setAccepting(false);
    }
  }, [decorated, selected, onAccept]);

  if (!hasEnrichment) return null;

  return (
    <div className={`rounded-lg border border-sky-200 bg-sky-50/70 ${className}`} data-testid={`ai-banner-${module}`}>
      <EnrichmentContextStrip context={context} className="m-2 mb-0" testId={`enrichment-strip-${module}`} />
      <div className="flex items-center justify-between gap-3 p-3">
        <div className="flex items-center gap-2 text-sm text-sky-900">
          <Sparkles className="w-4 h-4 text-sky-600" />
          <span>
            AI suggestions available based on your organization profile
            <span className="hidden sm:inline"> — {MODULE_LABELS[module] || module}</span>.
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!items ? (
            <Button size="sm" onClick={loadSuggestions} disabled={loading}
              data-testid={`ai-load-${module}`}>
              {loading ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Load Suggestions
            </Button>
          ) : (
            <button onClick={() => setExpanded((e) => !e)} className="text-sky-700">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {loading && !items && (
        <div className="px-3 pb-3 -mt-1" data-testid={`ai-loading-hint-${module}`}>
          <p className="text-xs text-sky-700/90 mb-1.5">
            Generating industry-specific suggestions — this can take 20–30 seconds...
          </p>
          <div className="h-1.5 w-full rounded-full bg-sky-100 overflow-hidden">
            <div className="h-full w-1/3 rounded-full bg-sky-400/80 ai-shimmer-bar" />
          </div>
        </div>
      )}

      {items && expanded && (
        <div className="px-3 pb-3 space-y-2" data-testid={`ai-suggestions-${module}`}>
          <div className="bg-white rounded-md border border-slate-200 divide-y divide-slate-100 max-h-72 overflow-auto">
            {decorated.map((it) => (
              <label key={it.id} className={`flex items-start gap-2 p-2.5 hover:bg-slate-50 cursor-pointer ${it.badgeState === "accepted" ? "opacity-60 bg-green-50/40" : ""}`}>
                {canAccept && (
                  <input type="checkbox" checked={!!selected[it.id] && it.badgeState !== "accepted"} className="mt-1"
                    disabled={it.badgeState === "accepted"}
                    onChange={(e) => setSelected((s) => ({ ...s, [it.id]: e.target.checked }))} />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-800 truncate">{it.label}</span>
                    {it.badgeState === "accepted" ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0" data-testid={`ai-badge-accepted-${it.id}`}>
                        <Check className="w-3 h-3 mr-0.5" />Accepted
                      </Badge>
                    ) : it.badgeState === "already_added" ? (
                      <Badge className="bg-slate-200 text-slate-600 text-[10px] shrink-0" data-testid={`ai-badge-already-${it.id}`}>Already Added</Badge>
                    ) : (
                      <Badge className="bg-sky-100 text-sky-700 text-[10px] shrink-0" data-testid={`ai-badge-suggested-${it.id}`}>Suggested</Badge>
                    )}
                  </div>
                  {it.detail && (
                    <div className="text-xs text-slate-500 mt-0.5 space-y-0.5">
                      {Object.entries(it.detail)
                        .filter(([k, v]) => !_TITLE_KEYS.includes(k) && v != null && v !== "" &&
                          !(Array.isArray(v) && v.length === 0))
                        .slice(0, 4)
                        .map(([k, v]) => (
                          <p key={k} className="line-clamp-2">
                            <span className="font-medium text-slate-600">{_fieldLabel(k)}:</span> {_formatVal(v)}
                          </p>
                        ))}
                    </div>
                  )}
                </div>
              </label>
            ))}
            {decorated.length === 0 && <p className="p-3 text-sm text-slate-400">No suggestions.</p>}
          </div>
          <div className="flex items-center gap-2">
            {canAccept ? (
              <>
                <Button size="sm" onClick={() => accept(false)} disabled={accepting} data-testid={`ai-accept-all-${module}`}>
                  {accepting ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                  {accepting ? "Adding…" : "Accept All"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => accept(true)} disabled={accepting}
                  data-testid={`ai-accept-selected-${module}`}>
                  Accept Selected
                </Button>
              </>
            ) : (
              <p className="text-xs text-slate-500" data-testid={`ai-advisory-${module}`}>
                These are AI suggestions for your review — add any that fit using the form below.
              </p>
            )}
            <Button size="sm" variant="ghost" className="text-slate-500"
              onClick={() => setItems(null)} disabled={accepting} data-testid={`ai-dismiss-${module}`}>
              <X className="w-4 h-4 mr-1" /> Dismiss
            </Button>
            <Button size="sm" variant="ghost" className="text-sky-600 ml-auto"
              onClick={loadSuggestions} disabled={loading || accepting}>
              <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
